/** @jsxImportSource @emotion/react */
import ikonLogoGold from '../assets/images/ikonLogoGold.svg';
import background from '../assets/images/backgroundDark.png';
import * as React from 'react';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { DROP_SHADOW, MediumText, UNSELECTABLE_TEXT, XLargeText } from '../styles/styles';
import {SearchBox} from '../guestSearch/SearchBox';
import { IKON_BLUE, WHITE } from '../styles/colors';
import { GUEST_SEARCH_ROUTE, ROOT_PATH } from '../core/routes';
import { Link, useNavigate } from 'react-router-dom';
import {HeaderMenu} from './HeaderMenu';
import {ResortSelectMenu} from './ResortSelectMenu';
import { getResource } from '../language/resources/getResource';
import { PreferencesMenu } from './PreferencesMenu';


const LogoTop = styled('img')`
  width: 180px;
  height: 80px;
  margin-right: 15px;
  ${UNSELECTABLE_TEXT}
`;

const SearchContainer = styled('div')`
  margin-top: auto;
  margin-bottom: auto;

  @media (min-width: 1054px) {
    flex-basis: 450px;
    flex-grow: 1;
  }
`;

const SignedInAs = styled('span')`
  color: ${WHITE};
  opacity: .9;
  ${MediumText};
`;

const Username = styled('span')`
  color: ${WHITE};
  ${XLargeText};
  align-self: flex-end;
`;

const HeaderContainer = styled('div')`
  background: url('${background}') no-repeat center center;
  background-size: cover;
  background-color: ${IKON_BLUE};
  background-attachment: initial;
  padding: 15px;
  ${DROP_SHADOW};
  
  @media (min-width: 640px) {
    background-attachment: fixed;
  }
`;

const HeaderContent = styled('div')`
  display: flex;
  flex-direction: column;
  flex-wrap: wrap;
  justify-content: space-between;
  
  /*
   * Switch from a row to a column on smaller screens so logo/logout/search stack instead of wrapping funny.
   */
  @media (min-width: 1054px) {
    flex-direction: row;
    align-items: flex-end;
  }
`;

const LoginContainer = styled('div')`
  margin-top: 20px;
  flex-grow: 1;
`;

/**
 * PageHeader: renders the page header (logo, search, user info, menus).
 *
 * React Router v6 version — using useNavigate()
 */
type PageHeaderProps = {
  currentUser: string;
  userRoleText: string;
  resources: Record<string, any>;
  enableLanguage: boolean;
  showResortSelectMenu: boolean;
};
const PageHeader: React.FC<PageHeaderProps> = React.memo(
  ({ currentUser, userRoleText, resources, enableLanguage, showResortSelectMenu }) => {
    const navigate = useNavigate();

    const onGuestSearched = (query: string) => {
      navigate(`${GUEST_SEARCH_ROUTE}/${encodeURIComponent(query)}`);
    };

    return (
      <HeaderContainer>
        <HeaderContent>
          <div css={css`margin-right: 10px; flex-grow: 1;`}>
            <Link to={ROOT_PATH} title={getResource(resources, 'PAGE_HEADER_BACK_TO_LANDING')}>
              <LogoTop src={ikonLogoGold} alt="IKON logo" />
            </Link>
          </div>

          <LoginContainer>
            <Username
              title={getResource(resources, 'PAGE_HEADER_SIGNED_IN_AS', currentUser)}
              id="current-user-message"
            >
              <SignedInAs>
                {getResource(
                  resources,
                  'PAGE_HEADER_SIGNED_IN_AS_WITH_ROLE',
                  currentUser,
                  userRoleText
                )}
              </SignedInAs>
            </Username>
            <HeaderMenu />
          </LoginContainer>

          {showResortSelectMenu && <ResortSelectMenu />}
          {enableLanguage && <PreferencesMenu />}

          <SearchContainer>
            <SearchBox onSearch={onGuestSearched} />
          </SearchContainer>
        </HeaderContent>
      </HeaderContainer>
    );
  }
);

export default PageHeader;
